import { fg } from '@atlaskit/platform-feature-flags/fg';
import { resolveActionKey } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type StreamResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { extractActionableFollowUpsMarkdownData } from './extractActionableFollowUpsMarkdownData';
import { extractMarkdownContent } from './extractMarkdownContent';
import { normalizeFinalizedMessagePresentation } from './normalizeFinalizedMessagePresentation';
import { preprocessStreamResponseDetails } from './preprocessStreamResponseDetails';
import type { ChatStoreContainerProps } from './types';

export const processStreamResponseMarkdown = ({
	streamMessage,
	messageId,
	conversationId,
	sendAnalyticsEvent,
	shouldPreprocessDetails = false,
}: {
	streamMessage: StreamResponse;
	messageId: string;
	conversationId: string;
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	/**
	 * When true, rewrite HTML `<details><summary>` blocks into the Rovo-native
	 * `▞…▚` boundary before the existing extractor runs. Callers gate this
	 * on `shouldPreprocessDetailsForConversation(...)`.
	 */
	shouldPreprocessDetails?: boolean;
}): {
	processedStreamMessage: StreamResponse;
	markdownData: Record<string, string>;
} => {
	if (fg('rovo_chat_action_render_analytics')) {
		const actions = streamMessage.message.message.actions ?? [];
		actions.forEach((action, actionIndex) => {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'received',
				actionSubject: 'rovoChatMessageAction',
				attributes: {
					analyticsVersion: 2,
					conversationId,
					messageId: streamMessage.message.message.id?.toString() ?? messageId,
					invocationId: action.invocationId,
					actionKey: action.key,
					resolvedActionKey: resolveActionKey(action),
					actionStatus: action.status,
					actionIndex,
					receivedActionCount: actions.length,
				},
			});
		});
	}

	if (fg('rovo_chat_history_lift')) {
		const normalizedPresentation = normalizeFinalizedMessagePresentation({
			message: streamMessage,
			options: {
				shouldExtractA2UI: false,
				shouldExtractActionableFollowUps: true,
				shouldExtractDataSculptor: fg('rovo_enable_data_sculptor'),
				shouldExtractInlineLumina: fg('rovo_chat_enable_inline_lumina'),
				shouldExtractLegacyChartBoundaries: false,
				shouldPreprocessDetails,
			},
		});

		if (normalizedPresentation.extractionCounts.expand > 0) {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'viewed',
				actionSubject: 'expandComponent',
				attributes: {
					messageId,
					conversationId,
					expandCount: normalizedPresentation.extractionCounts.expand,
					timestamp: new Date().toISOString(),
					isFinalResponse: true,
				},
			});
		}

		if (normalizedPresentation.extractionCounts.unicodeChart > 0) {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'extracted',
				actionSubject: 'vizPluginChart',
				attributes: {
					messageId,
					conversationId,
					chartCount: normalizedPresentation.extractionCounts.unicodeChart,
					timestamp: new Date().toISOString(),
				},
			});
		}

		return {
			processedStreamMessage: normalizedPresentation.message,
			markdownData: normalizedPresentation.markdownData,
		};
	} else {
		let processedStreamMessage = streamMessage;
		const markdownData: Record<string, string> = {};

		// Applies to the final-response path (non-streaming) and to reconnect replay.
		if (shouldPreprocessDetails) {
			processedStreamMessage = preprocessStreamResponseDetails(processedStreamMessage);
		}

		if (fg('rovo_enable_data_sculptor')) {
			const dataSculptorMsg = extractMarkdownContent({
				streamMessage: processedStreamMessage,
				boundaryStart: '[^sculptor:start^]',
				boundaryEnd: '[^sculptor:end^]',
				replacementContent: '[^sculptor^]',
			});
			processedStreamMessage = dataSculptorMsg.processedStreamMessage as StreamResponse;
			dataSculptorMsg.boundaryContents.forEach((content, index) => {
				markdownData[`sculptor-${index}`] = content;
			});
		}

		const expandOpenTag = '▞';
		const expandCloseTag = '▚';
		const expandPlaceholder = '[^expand^]';

		const expandMsg = extractMarkdownContent({
			streamMessage: processedStreamMessage,
			boundaryStart: expandOpenTag,
			boundaryEnd: expandCloseTag,
			replacementContent: expandPlaceholder,
		});
		processedStreamMessage = expandMsg.processedStreamMessage as StreamResponse;
		expandMsg.boundaryContents.forEach((content, index) => {
			markdownData[`expand-${index}`] = content;
		});

		if (expandMsg?.boundaryContents && expandMsg.boundaryContents.length > 0) {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'viewed',
				actionSubject: 'expandComponent',
				attributes: {
					messageId,
					conversationId,
					expandCount: expandMsg.boundaryContents.length,
					timestamp: new Date().toISOString(),
					isFinalResponse: true,
				},
			});
		}

		const chartOpenTag = '◴';
		const chartCloseTag = '◶';
		const chartPlaceholder = '[^chart^]';
		const chartMsg = extractMarkdownContent({
			streamMessage: processedStreamMessage,
			boundaryStart: chartOpenTag,
			boundaryEnd: chartCloseTag,
			replacementContent: chartPlaceholder,
		});
		processedStreamMessage = chartMsg.processedStreamMessage as StreamResponse;
		chartMsg.boundaryContents.forEach((content, index) => {
			markdownData[`chart-${index}`] = content;
		});

		if (chartMsg?.boundaryContents && chartMsg.boundaryContents.length > 0) {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'extracted',
				actionSubject: 'vizPluginChart',
				attributes: {
					messageId,
					conversationId,
					chartCount: chartMsg.boundaryContents.length,
					timestamp: new Date().toISOString(),
				},
			});
		}

		if (fg('rovo_chat_enable_inline_lumina')) {
			const luminaMsg = extractMarkdownContent({
				streamMessage: processedStreamMessage,
				boundaryStart: '◐',
				boundaryEnd: '◑',
				replacementContent: '\n\n[^lumina^]\n\n',
			});
			processedStreamMessage = luminaMsg.processedStreamMessage as StreamResponse;
			luminaMsg.boundaryContents.forEach((content, index) => {
				markdownData[`lumina-${index}`] = content;
			});
		}

		processedStreamMessage = extractActionableFollowUpsMarkdownData({
			streamMessage: processedStreamMessage,
			markdownData,
		});

		return {
			processedStreamMessage,
			markdownData,
		};
	}
};
